<tag-editor>
  <!--
    Props
    ──────────────────────────────────────────────────────────────────
    editing      {boolean}   – false -> read-only badge renderer
    value        {Array}     – { text: string, color?: string }[]
    colors       {Object}    – Record<string, { name?, foreground, background }> | { key, name, foreground, background[] }
    defaultColor {Object}    – { foreground: string, background: string }
    showDefault  {boolean}   - false -> no default color if there are any colors
    onAdd        {Function}  – ()
    onDelete     {Function}  – (index: number)
    onChange     {Function}  – (index: number, tag: Tag)
    onSort       {Function}  – (oldIndex: number, newIndex: number)

    Usage example
    ──────────────────────────────────────────────────────────────────
    <tag-editor
      editing="{ isEditing }"
      value="{ tags }"
      colors="{ palette }"
      default-color="{ { foreground: '#374151', background: '#e5e7eb' } }"
      on-add="{ handleAdd }"
      on-delete="{ handleDelete }"
      on-change="{ handleChange }"
      on-sort="{ handleSort }"
    />
  -->

  <div class="te">

    <!-- ══════════════ VIEW MODE ══════════════ -->
    <div if="{ !props.editing }" class="te-row">
      <span if="{ !(props.value && props.value.length) }" class="te-empty">タグなし</span>
      <span each="{ (tag, i) in (props.value || []) }" key="{ i }" class="te-badge"
        style="{ this.createStyleAttr(tag.color) }">
        { tag.text }
      </span>
    </div>

    <!-- ══════════════ EDIT MODE ══════════════ -->
    <div if="{ props.editing }" class="te-row">

      <div each="{ (tag, i) in (props.value || []) }" key="{ i }"
        class="te-chip{ state.dragSrc === i ? ' is-dragging' : '' }{ state.dragOver === i && state.dragSrc !== i ? ' is-dragover' : '' }"
        style="{ this.createStyleAttr(tag.color) }" draggable="true" ondragstart="{ (e) => dndStart(e, i) }"
        ondragover="{ (e) => dndOver(e, i) }" ondragleave="{ (e) => dndLeave(e, i) }" ondrop="{ (e) => dndDrop(e, i) }"
        ondragend="{ dndEnd }">
        <!-- Drag grip -->
        <span class="te-grip" title="Drag to reorder">⠿</span>

        <!-- Editable label -->
        <input class="te-text" type="text" value="{ tag.text }" size="{ Math.max((tag.text || '').length + 2, 4) }"
          onfocus="{ (e) => txtFocus(e, i) }" oninput="{ (e) => txtInput(e, i) }" onblur="{ (e) => txtBlur(e, i) }"
          onkeydown="{ (e) => txtKey(e, i) }" ondragstart="{ noop }" />

        <!-- Color swatch + dropdown -->
        <div class="te-cpick">
          <button class="te-tool swatch" style="{ this.createStyleAttr(tag.color) }" onclick="{ handleSwatchClick(i) }"
            title="Change color">
            <i class="fa fa-eye-dropper"></i>
          </button>

          <!--
            The dropdown uses state.cpOpen (the index of the open chip) rather
            than `i` from the outer each so that the inner each over state.colors
            doesn't need to close over the outer loop variable — avoiding any
            Riot 4 nested-each scope surprises.
          -->
          <div if="{ state.cpOpen === i }" class="te-dropdown" onclick="{ absorbClick }">
            <div class="te-dd-grid">
              <!-- Default (no color key) -->
              <button if="{ state.showDefault }"
                class="te-copt{ !(props.value[state.cpOpen] || {}).color ? ' is-active' : '' }"
                style="{ this.createStyleAttr(this.defColor()) }"
                onclick="{ handleApplyColor(state.cpOpen, undefined) }">ディフォルト</button>
              <!-- Named palette entries -->
              <button each="{ c in state.colorList }" key="{ c.key }"
                class="te-copt{ (props.value[state.cpOpen] || {}).color === c.key ? ' is-active' : '' }"
                style="{ this.createStyleAttr(c) }" onclick="{ handleApplyColor(state.cpOpen, c.key) }">
                { c.name || c.key }
              </button>
            </div>
          </div>
        </div>

        <!-- Remove -->
        <button class="te-tool" onclick="{ handleDelTag(i) }" title="Remove tag">✕</button>
      </div>

      <!-- Add new tag -->
      <button class="te-add" onclick="{ addTag }">+ 追加</button>

    </div>
  </div>

  <script>

    function getNormalizedColorListAndMap(c) {
      if (Array.isArray(c)) {
        return [c, Object.fromEntries(c.map(x => [x.key, x]))];
      }
      return [Object.entries(c).map(([key, value]) => ({ ...value, key })), c];
    }

    export default {
      /* ─────────────────────────────────────────────────────────────
        Initial state
      ───────────────────────────────────────────────────────────── */
      state: {
        cpOpen: null,  // index of chip whose color-picker is open, or null
        dragSrc: null,  // index of chip being dragged
        dragOver: null,  // index of chip currently under the drag pointer
        liveText: {},    // { [index]: string } uncommitted keystrokes
      },

      /* ─────────────────────────────────────────────────────────────
        Color helpers
      ───────────────────────────────────────────────────────────── */

      /** Returns the resolved { foreground, background } for a color key. */
      colorOf(key) {
        if (key && typeof key === 'object') {
          return key;
        }
        const map = this.state.colorMap || {};
        return (key && map[key]) ? map[key] : this.defColor();
      },

      /** Returns the default color object from props, with a built-in fallback. */
      defColor() {
        return this.props.defaultColor || { foreground: '#374151', background: '#e5e7eb' };
      },

      /* ─────────────────────────────────────────────────────────────
        Generic event utilities
      ───────────────────────────────────────────────────────────── */

      /** Prevents the browser from starting a text-selection drag on the input. */
      noop(e) {
        e.preventDefault();
      },

      /** Stops the dropdown's click from bubbling to the document dismiss handler. */
      absorbClick(e) {
        e.stopPropagation();
      },

      /* ─────────────────────────────────────────────────────────────
        Text editing
          Strategy: mutate liveText silently on each keystroke (no
          re-render → no cursor jump), then commit to the parent on blur.
      ───────────────────────────────────────────────────────────── */

      txtFocus(e, i) {
        const tag = (this.props.value || [])[i];
        // Seed live buffer with the last committed value
        this.state.liveText[i] = tag ? tag.text : '';
      },

      txtInput(e, i) {
        // Intentionally NOT calling this.update() here.
        // Direct mutation keeps the cursor stable while typing.
        this.state.liveText[i] = e.target.value;
      },

      txtBlur(e, i) {
        const hasLive = Object.prototype.hasOwnProperty.call(this.state.liveText, i);
        const live = hasLive ? this.state.liveText[i] : null;

        // Clean up the live buffer entry
        const next = { ...this.state.liveText };
        delete next[i];

        const tag = (this.props.value || [])[i];
        if (tag && live !== null && this.props.onChange) {
          this.props.onChange(i, { ...tag, text: live });
        }
        this.update();
      },

      txtKey(e, i) {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.target.blur();   // triggers txtBlur → commits
        }
        if (e.key === 'Escape') {
          // Discard edits: restore DOM value and blur without committing
          const tag = (this.props.value || [])[i];
          const next = { ...this.state.liveText };
          delete next[i];
          this.update({ liveText: next });
          if (tag) e.target.value = tag.text;
          e.target.blur();
        }
      },

      /* ─────────────────────────────────────────────────────────────
        Color picker
      ───────────────────────────────────────────────────────────── */


      swatchClick(i) {
        this.update({ cpOpen: this.state.cpOpen === i ? null : i });
      },

      handleSwatchClick(i) {
        return (e) => {
          e?.preventDefault?.();
          e?.stopPropagation?.();
          return this.swatchClick(i);
        };
      },

      applyColor(i, key) {
        const tag = (this.props.value || [])[i];
        if (!tag) {
          this.update({ cpOpen: null }); return;
        }

        const updated = { ...tag };
        if (key === undefined) {
          delete updated.color;
        } else {
          updated.color = key;
        }

        if (this.props.onChange) this.props.onChange(i, updated);
        this.update({ cpOpen: null });
      },

      handleApplyColor(i, key) {
        return e => {
          e.preventDefault();
          e.stopPropagation();
          return this.applyColor(i, key);
        };
      },

      createStyleAttr(colorObject) {
        const c = this.colorOf(colorObject);
        return `background:${c.background};color:${c.foreground}`;
      },

      /* ─────────────────────────────────────────────────────────────
        Add / delete
      ───────────────────────────────────────────────────────────── */

      addTag(e) {
        e?.preventDefault?.();
        this.props.onAdd?.();
        this.update();
      },

      delTag(i) {
        this.props.onDelete?.(i);
        this.update();
      },

      handleDelTag(i) {
        return e => {
          e.preventDefault();
          return this.delTag(i);
        };
      },

      /* ─────────────────────────────────────────────────────────────
        Drag-and-drop reordering (HTML5 DnD API)
          Semantics: onSort(oldIndex, newIndex) where newIndex is the
          position of the drop target in the original array.
      ───────────────────────────────────────────────────────────── */

      dndStart(e, i) {
        this.update({ dragSrc: i });
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(i));
      },

      dndOver(e, i) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (this.state.dragOver !== i) this.update({ dragOver: i });
      },

      dndLeave(e, i) {
        // Only clear when truly leaving the chip (not entering a child element)
        if (this.state.dragOver === i && !e.currentTarget.contains(e.relatedTarget)) {
          this.update({ dragOver: null });
        }
      },

      dndDrop(e, i) {
        e.preventDefault();
        e.stopPropagation();
        const src = this.state.dragSrc;
        this.update({ dragSrc: null, dragOver: null });
        if (src !== null && src !== i && this.props.onSort) {
          this.props.onSort(src, i);
        }
      },

      dndEnd() {
        // Fires even when drop is cancelled (e.g. released outside a drop zone)
        this.update({ dragSrc: null, dragOver: null });
      },

      /* ─────────────────────────────────────────────────────────────
        Lifecycle
      ───────────────────────────────────────────────────────────── */

      updateValueFromProps(force) {
        this.state.refs ||= {};
        const changed = this.state.refs.showDefault !== this.props.showDefault
          || this.state.refs.colors !== this.props.colors;
        if (!changed && !force) {
          return;
        }
        this.state.refs.showDefault = this.props.showDefault;
        this.state.refs.colors = this.props.colors;

        const [list, map] = getNormalizedColorListAndMap(this.props.colors);
        this.state.colorList = list;
        this.state.colorMap = map;
        this.state.showDefault = this.props.showDefault
          ? true
          : !this.state.colorList?.length;
      },

      onBeforeMount() {
        this.updateValueFromProps(true);
      },

      onBeforeUpdate() {
        this.updateValueFromProps(false);
      },

      onMounted() {
        // Close the color picker when clicking anywhere outside the component
        this._dismiss = () => {
          if (this.state.cpOpen !== null) this.update({ cpOpen: null });
        };
        document.addEventListener('click', this._dismiss);
      },

      onBeforeUnmount() {
        document.removeEventListener('click', this._dismiss);
      },

    };
  </script>

  <style>
    /* Component host */
    tag-editor {
      display: block;
    }

    .te {
      font-family: inherit;
      font-size: 13px;
    }

    /* ── Shared flex row ─────────────────────────────────────── */
    .te-row {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 6px;
      min-height: 30px;
    }

    /* ── View-mode badges ────────────────────────────────────── */
    .te-badge {
      display: inline-flex;
      align-items: center;
      padding: 3px 11px;
      border-radius: 9999px;
      font-size: 13px;
      font-weight: 500;
      line-height: 1.4;
      white-space: nowrap;
    }

    .te-empty {
      color: #9ca3af;
      font-style: italic;
    }

    /* ── Edit-mode chips ─────────────────────────────────────── */
    .te-chip {
      position: relative;
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 3px 4px;
      border-radius: 9999px;
      line-height: 1.4;
      font-weight: 500;
      cursor: grab;
      outline: 2px solid transparent;
      outline-offset: 1px;
      transition: opacity 0.15s, outline-color 0.12s;
      user-select: none;
    }

    .te-chip:active {
      cursor: grabbing;
    }

    .te-chip.is-dragging {
      opacity: 0.25;
      cursor: grabbing;
    }

    .te-chip.is-dragover {
      outline-color: var(--te-fg, #374151);
    }

    .te-grip {
      opacity: 0.4;
      font-size: 14px;
      line-height: 1;
      flex-shrink: 0;
      cursor: grab;
    }

    /* ── Text input ──────────────────────────────────────────── */
    .te-text {
      border: none;
      outline: none;
      background: transparent;
      color: inherit;
      font: inherit;
      padding: 0;
      min-width: 24px;
      width: min-content;
      cursor: text;
      caret-color: currentColor;
    }

    .te-text:focus {
      border-bottom: 1.5px dashed currentColor;
    }

    /* ── Color swatch ────────────────────────────────────────── */
    .te-cpick {
      position: relative;
      display: inline-flex;
      align-items: center;
      flex-shrink: 0;
    }

    /* ── Color dropdown ──────────────────────────────────────── */
    .te-dropdown {
      position: absolute;
      top: calc(100% + 9px);
      left: 50%;
      transform: translateX(-50%);
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 10px;
      z-index: 999;
      box-shadow: 0 6px 22px rgba(0, 0, 0, 0.13);
      min-width: 152px;
    }

    .te-dd-label {
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #9ca3af;
      margin-bottom: 8px;
    }

    .te-dd-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 5px;
    }

    .te-copt {
      padding: 3px 9px;
      border-radius: 9999px;
      border: 2px solid transparent;
      font-size: 12px;
      font-weight: 500;
      font-family: inherit;
      cursor: pointer;
      white-space: nowrap;
      transition: opacity 0.1s;
    }

    .te-copt:hover {
      opacity: 0.78;
    }

    .te-copt.is-active {
      border-color: rgba(0, 0, 0, 0.28);
    }

    /* ── Delete button ───────────────────────────────────────── */
    .te-tool {
      background: none;
      border: none;
      color: inherit;
      cursor: pointer;
      font-size: 11px;
      padding: 1px 2px;
      line-height: 1;
      opacity: 0.48;
      transition: opacity 0.12s;
      flex-shrink: 0;
    }

    .te-tool:hover {
      opacity: 1;
    }

    /* ── Add-tag button ──────────────────────────────────────── */
    .te-add {
      display: inline-flex;
      align-items: center;
      padding: 3px 11px;
      border: 1.5px dashed #d1d5db;
      background: none;
      border-radius: 9999px;
      cursor: pointer;
      font-size: 13px;
      font-family: inherit;
      color: #6b7280;
      transition: border-color 0.15s, color 0.15s;
    }

    .te-add:hover {
      border-color: #9ca3af;
      color: #374151;
    }
  </style>
</tag-editor>